import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { createProfile, createTimeslot, Recipient } from '@/lib/forward-api';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface AddProfileDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess: () => void;
    recipients: Recipient[];
}

interface TimeSlot {
    days: string[];
    startTime: string;
    endTime: string;
    recipientId: string;
}

const DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const DAY_MAPPING: Record<string, string> = {
    'Mo': 'MONDAY',
    'Tu': 'TUESDAY',
    'We': 'WEDNESDAY',
    'Th': 'THURSDAY',
    'Fr': 'FRIDAY',
    'Sa': 'SATURDAY',
    'Su': 'SUNDAY'
};

export function AddProfileDialog({ open, onOpenChange, onSuccess, recipients }: AddProfileDialogProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [loading, setLoading] = useState(false);
    const [title, setTitle] = useState('');
    const [timeslots, setTimeslots] = useState<TimeSlot[]>([{
        days: [],
        startTime: '00:00',
        endTime: '23:59',
        recipientId: recipients[0]?.id || ''
    }]);

    const toggleDay = (slotIndex: number, day: string) => {
        setTimeslots(slots => {
            const newSlots = [...slots];
            const slot = newSlots[slotIndex];
            if (slot.days.includes(day)) {
                slot.days = slot.days.filter(d => d !== day);
            } else {
                slot.days = [...slot.days, day];
            }
            return newSlots;
        });
    };

    const addTimeSlot = () => {
        setTimeslots([...timeslots, {
            days: [],
            startTime: '00:00',
            endTime: '23:59',
            recipientId: recipients[0]?.id || ''
        }]);
    };

    const addAnotherHandover = () => {
        setTimeslots([...timeslots, {
            days: ['Su'],
            startTime: '10:00',
            endTime: '20:00',
            recipientId: recipients[0]?.id || ''
        }]);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!title || timeslots.length === 0 || !timeslots[0].recipientId) {
            toast({
                title: t('error.title'),
                description: t('forward.validationError'),
                variant: 'destructive'
            });
            return;
        }

        setLoading(true);
        try {
            // Create profile first
            const response = await createProfile({
                title,
                receiverId: timeslots[0].recipientId
            });

            const profileId = response.resultObject?.id;

            if (profileId) {
                // Create timeslots for the profile
                const timeslotPromises = timeslots.flatMap(slot =>
                    slot.days.map(day =>
                        createTimeslot(profileId, {
                            receiverId: slot.recipientId,
                            dayOfWeek: DAY_MAPPING[day],
                            startTime: slot.startTime,
                            endTime: slot.endTime
                        })
                    )
                );

                await Promise.all(timeslotPromises);
            }

            toast({
                title: t('success.title'),
                description: t('forward.profileCreated')
            });
            setTitle('');
            setTimeslots([{ days: [], startTime: '00:00', endTime: '23:59', recipientId: recipients[0]?.id || '' }]);
            onSuccess();
            onOpenChange(false);
        } catch (error) {
            toast({
                title: t('error.title'),
                description: t('forward.profileCreateError'),
                variant: 'destructive'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{t('forward.addProfile')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <Label htmlFor="profile-title">{t('forward.nameYourProfile')}</Label>
                        <Input
                            id="profile-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Off hours"
                        />
                    </div>

                    {timeslots.map((slot, index) => (
                        <div key={index} className="space-y-4 p-4 border rounded-lg">
                            <div>
                                <Label>{t('forward.handoverRecipient')}</Label>
                                <Select
                                    value={slot.recipientId}
                                    onValueChange={(value) => {
                                        setTimeslots(slots => {
                                            const newSlots = [...slots];
                                            newSlots[index].recipientId = value;
                                            return newSlots;
                                        });
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {recipients.map(r => (
                                            <SelectItem key={r.id} value={r.id}>{r.title}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label>{t('forward.repeatAt')}</Label>
                                <div className="flex gap-2 mt-2">
                                    {DAYS.map((day) => (
                                        <button
                                            key={day}
                                            type="button"
                                            onClick={() => toggleDay(index, day)}
                                            className={`w-10 h-10 rounded-full border text-sm font-medium transition-colors ${slot.days.includes(day)
                                                ? 'bg-gray-900 text-white border-gray-900'
                                                : 'bg-white text-gray-600 border-gray-300'
                                                }`}
                                        >
                                            {day}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <Label>{t('forward.activeTimes')}</Label>
                                <div className="flex items-center gap-4 mt-2">
                                    <Input
                                        type="time"
                                        value={slot.startTime}
                                        onChange={(e) => {
                                            setTimeslots(slots => {
                                                const newSlots = [...slots];
                                                newSlots[index].startTime = e.target.value;
                                                return newSlots;
                                            });
                                        }}
                                    />
                                    <span>{t('activityPage.to')}</span>
                                    <Input
                                        type="time"
                                        value={slot.endTime}
                                        onChange={(e) => {
                                            setTimeslots(slots => {
                                                const newSlots = [...slots];
                                                newSlots[index].endTime = e.target.value;
                                                return newSlots;
                                            });
                                        }}
                                    />
                                </div>
                            </div>

                            {index === timeslots.length - 1 && (
                                <Button
                                    type="button"
                                    variant="link"
                                    onClick={addTimeSlot}
                                    className="text-brand-primary p-0 h-auto"
                                >
                                    <Plus className="h-4 w-4 mr-1" />
                                    {t('forward.addTimeSpan')}
                                </Button>
                            )}
                        </div>
                    ))}

                    <Button
                        type="button"
                        variant="link"
                        onClick={addAnotherHandover}
                        className="text-brand-primary p-0 h-auto"
                    >
                        <Plus className="h-4 w-4 mr-1" />
                        {t('forward.addHandover')}
                    </Button>

                    <div className="flex justify-end">
                        <Button type="submit" disabled={loading}>
                            {t('forward.addProfile')}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
